"use client";

import { ArrowUpRight } from "lucide-react";
import { useRef, type ReactNode } from "react";
import { useSpineSlice } from "@/components/site/motion/useSpineSlice";
import { useContent } from "@/components/site/ContentProvider";
import { localeHref, serviceHref } from "@/content/index";
import styles from "./services-bento.module.css";

/**
 * Full-row link that ignores click if the finger moved (scroll intent).
 * Sticky :hover + accidental navigation were trapping mobile scroll.
 */
function ServiceRowLink({
  href,
  className,
  children,
}: {
  href: string;
  className: string;
  children: ReactNode;
}) {
  const origin = useRef<{ x: number; y: number } | null>(null);
  const dragged = useRef(false);

  return (
    <a
      className={className}
      href={href}
      onPointerDown={(e) => {
        origin.current = { x: e.clientX, y: e.clientY };
        dragged.current = false;
      }}
      onPointerMove={(e) => {
        const o = origin.current;
        if (!o) return;
        if (Math.hypot(e.clientX - o.x, e.clientY - o.y) > 12) {
          dragged.current = true;
        }
      }}
      onPointerUp={() => {
        origin.current = null;
      }}
      onPointerCancel={() => {
        origin.current = null;
        dragged.current = true;
      }}
      onClick={(e) => {
        if (dragged.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      {children}
    </a>
  );
}

export function Services() {
  useSpineSlice((m) => m.spine!.buildServices());

  const site = useContent();
  const servicesHref = localeHref(site.locale, "services");

  return (
    <div
      className={`landing_content services_content f a-c j-c ${styles.wrap}`}
      id="services"
      data-bento="0"
    >
      <div className={`services_table f f-c ${styles.table}`}>
        <div className={styles.manifest}>
          {site.services.map((service, i) => (
            <ServiceRowLink
              className={`services_row ${styles.row}${i === 0 ? " active" : ""}`}
              key={service.id}
              href={serviceHref(site.locale, service.id)}
            >
              <div className={styles.rowInner}>
                <span className={styles.num} dir="ltr">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className={styles.title}>{service.name}</span>
                <span className={styles.chev} aria-hidden>
                  <ArrowUpRight className={styles.chevIcon} strokeWidth={1.75} />
                </span>
              </div>
            </ServiceRowLink>
          ))}
        </div>
        {/* Outside `.services_row` so mosaic/row GSAP never owns this CTA. */}
        <div className={styles.viewAll}>
          <a href={servicesHref} className={`site_btn f a-c j-c ${styles.viewAllBtn}`}>
            <strong className="label">{site.labels.viewAllServices}</strong>
          </a>
        </div>
      </div>
    </div>
  );
}
